import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Home as HomeIcon, ArrowLeft, CalendarDays, ShoppingCart, Pill, StickyNote, Plus, Trash2, X, CalendarIcon } from 'lucide-react';
import { homeApi, tokenApi } from '@/lib/api';
import { toast } from 'sonner';
import { formatDate, isAuthenticated, cn } from '@/lib/utils';
import { format } from 'date-fns';
import SponsorSection from '@/components/SponsorSection';
import { ModuleChat } from '@/components/ModuleChat';

const STICKY_COLORS = {
  yellow: 'bg-yellow-100 border-yellow-200',
  pink: 'bg-pink-100 border-pink-200',
  blue: 'bg-blue-100 border-blue-200',
  green: 'bg-green-100 border-green-200'
};

export default function HomeModule() {
  const { tokenId } = useParams();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('calendar');
  const [isLoading, setIsLoading] = useState(true);
  
  // Data
  const [events, setEvents] = useState([]);
  const [groceries, setGroceries] = useState([]);
  const [medications, setMedications] = useState([]);
  const [stickyNotes, setStickyNotes] = useState([]);
  const [sponsorInfo, setSponsorInfo] = useState(null);
  
  // Dialogs
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [showAddGrocery, setShowAddGrocery] = useState(false);
  const [showAddMedication, setShowAddMedication] = useState(false);
  const [showAddSticky, setShowAddSticky] = useState(false);
  
  // Form states
  const [eventTitle, setEventTitle] = useState('');
  const [eventDate, setEventDate] = useState(null);
  const [eventMember, setEventMember] = useState('');
  
  const [groceryName, setGroceryName] = useState('');
  const [groceryQuantity, setGroceryQuantity] = useState('1');
  
  const [medName, setMedName] = useState('');
  const [medDosage, setMedDosage] = useState('');
  const [medSchedule, setMedSchedule] = useState('');
  const [medMember, setMedMember] = useState('');
  
  const [stickyContent, setStickyContent] = useState('');
  const [stickyColor, setStickyColor] = useState('yellow');
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate(`/h/${tokenId}`);
      return;
    }
    loadAllData();
    loadSponsorInfo();
  }, [tokenId]);

  const loadAllData = async () => {
    try {
      const [eventsRes, groceriesRes, medsRes, stickiesRes] = await Promise.all([
        homeApi.getEvents(tokenId),
        homeApi.getGroceryItems(tokenId),
        homeApi.getMedications(tokenId),
        homeApi.getStickyNotes(tokenId)
      ]);
      setEvents(eventsRes.data);
      setGroceries(groceriesRes.data);
      setMedications(medsRes.data);
      setStickyNotes(stickiesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSponsorInfo = async () => {
    try {
      const response = await tokenApi.getSponsorInfo(tokenId, 'home');
      setSponsorInfo(response.data);
    } catch (error) {
      console.log('No sponsor info available');
    }
  };

  // Event handlers
  const handleAddEvent = async () => {
    if (!eventTitle || !eventDate) return;
    setIsSubmitting(true);
    try {
      await homeApi.createEvent(tokenId, eventTitle, null, eventDate.toISOString(), null, eventMember || null);
      toast.success('Event added!');
      setShowAddEvent(false);
      setEventTitle('');
      setEventDate(null);
      setEventMember('');
      loadAllData();
    } catch (error) {
      toast.error('Failed to add event');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await homeApi.deleteEvent(eventId);
      toast.success('Event deleted');
      loadAllData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  // Grocery handlers
  const handleAddGrocery = async () => {
    if (!groceryName) return;
    setIsSubmitting(true);
    try {
      await homeApi.createGroceryItem(tokenId, groceryName, parseInt(groceryQuantity) || 1);
      toast.success('Item added!');
      setShowAddGrocery(false);
      setGroceryName('');
      setGroceryQuantity('1');
      loadAllData();
    } catch (error) {
      toast.error('Failed to add item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleGrocery = async (itemId, currentChecked) => {
    try {
      await homeApi.toggleGroceryItem(itemId, !currentChecked);
      loadAllData();
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const handleDeleteGrocery = async (itemId) => {
    try {
      await homeApi.deleteGroceryItem(itemId);
      toast.success('Item removed');
      loadAllData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  // Medication handlers
  const handleAddMedication = async () => {
    if (!medName) return;
    setIsSubmitting(true);
    try {
      await homeApi.createMedication(tokenId, medName, medDosage || null, medSchedule || null, medMember || null);
      toast.success('Medication added!');
      setShowAddMedication(false);
      setMedName('');
      setMedDosage('');
      setMedSchedule('');
      setMedMember('');
      loadAllData();
    } catch (error) {
      toast.error('Failed to add medication');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMedication = async (medId) => {
    try {
      await homeApi.deleteMedication(medId);
      toast.success('Medication removed');
      loadAllData();
    } catch (error) {
      toast.error('Failed to delete medication');
    }
  };

  // Sticky note handlers
  const handleAddSticky = async () => {
    if (!stickyContent) return;
    setIsSubmitting(true);
    try {
      await homeApi.createStickyNote(tokenId, stickyContent, stickyColor);
      toast.success('Note added!');
      setShowAddSticky(false);
      setStickyContent('');
      setStickyColor('yellow');
      loadAllData();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSticky = async (noteId) => {
    try {
      await homeApi.deleteStickyNote(noteId);
      toast.success('Note removed');
      loadAllData();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="home-module">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="back-to-dashboard">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <HomeIcon className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h1 className="font-bold text-lg text-slate-900">Memora Home Hub</h1>
                <p className="text-xs text-slate-500 font-mono">{tokenId}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="bg-white border border-slate-100 rounded-full p-1 inline-flex">
            <TabsTrigger 
              value="calendar" 
              className="rounded-full px-6 data-[state=active]:bg-slate-900 data-[state=active]:text-white"
              data-testid="tab-calendar"
            >
              <CalendarDays className="w-4 h-4 mr-2" />
              Calendar
            </TabsTrigger>
            <TabsTrigger 
              value="groceries" 
              className="rounded-full px-6 data-[state=active]:bg-slate-900 data-[state=active]:text-white"
              data-testid="tab-groceries"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Groceries
            </TabsTrigger>
            <TabsTrigger 
              value="medications" 
              className="rounded-full px-6 data-[state=active]:bg-slate-900 data-[state=active]:text-white"
              data-testid="tab-medications"
            >
              <Pill className="w-4 h-4 mr-2" />
              Medicine
            </TabsTrigger>
            <TabsTrigger 
              value="sticky" 
              className="rounded-full px-6 data-[state=active]:bg-slate-900 data-[state=active]:text-white"
              data-testid="tab-sticky"
            >
              <StickyNote className="w-4 h-4 mr-2" />
              Notes
            </TabsTrigger>
          </TabsList>

          {/* Calendar Tab */}
          <TabsContent value="calendar" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">Family Calendar</h2>
              <Button 
                onClick={() => setShowAddEvent(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                data-testid="add-event-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Event
              </Button>
            </div>

            {events.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
                <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No upcoming events</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100">
                {events.map((event, index) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.03 }}
                    className="p-4 flex items-center gap-4"
                    data-testid={`event-${event.id}`}
                  >
                    <div className="w-12 h-12 bg-emerald-50 rounded-xl flex flex-col items-center justify-center flex-shrink-0">
                      <span className="text-xs text-emerald-600 font-medium">
                        {format(new Date(event.event_date), 'MMM')}
                      </span>
                      <span className="text-lg font-bold text-emerald-700">
                        {format(new Date(event.event_date), 'd')}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900">{event.title}</p>
                      {event.family_member && (
                        <p className="text-sm text-slate-500">{event.family_member}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteEvent(event.id)}
                      className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Groceries Tab */}
          <TabsContent value="groceries" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">Grocery List</h2>
              <Button 
                onClick={() => setShowAddGrocery(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                data-testid="add-grocery-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>

            {groceries.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
                <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Your grocery list is empty</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100">
                {groceries.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.03 }}
                    className="p-4 flex items-center gap-4"
                    data-testid={`grocery-${item.id}`}
                  >
                    <Checkbox
                      checked={item.is_checked}
                      onCheckedChange={() => handleToggleGrocery(item.id, item.is_checked)}
                      className="w-5 h-5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className={cn(
                        "font-medium",
                        item.is_checked ? "text-slate-400 line-through" : "text-slate-900"
                      )}>
                        {item.name}
                      </p>
                      {item.quantity > 1 && (
                        <p className="text-sm text-slate-500">Qty: {item.quantity}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteGrocery(item.id)}
                      className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Medications Tab */}
          <TabsContent value="medications" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">Medicine Tracker</h2>
              <Button 
                onClick={() => setShowAddMedication(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                data-testid="add-medication-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Medicine
              </Button>
            </div>

            {medications.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
                <Pill className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No medications tracked</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {medications.map((med, index) => (
                  <motion.div
                    key={med.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white rounded-xl border border-slate-100 p-4"
                    data-testid={`medication-${med.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Pill className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{med.name}</p>
                          {med.dosage && <p className="text-sm text-slate-500">{med.dosage}</p>}
                          {med.schedule && <p className="text-sm text-emerald-600">{med.schedule}</p>}
                          {med.family_member && <p className="text-xs text-slate-400 mt-1">For: {med.family_member}</p>}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteMedication(med.id)}
                        className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Sticky Notes Tab */}
          <TabsContent value="sticky" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">Sticky Notes</h2>
              <Button 
                onClick={() => setShowAddSticky(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                data-testid="add-sticky-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Note
              </Button>
            </div>

            {stickyNotes.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
                <StickyNote className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No sticky notes yet</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-4">
                {stickyNotes.map((note, index) => (
                  <motion.div
                    key={note.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className={cn(
                      "rounded-xl border p-4 relative min-h-[150px]",
                      STICKY_COLORS[note.color] || STICKY_COLORS.yellow
                    )}
                    data-testid={`sticky-${note.id}`}
                  >
                    <button
                      onClick={() => handleDeleteSticky(note.id)}
                      className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <p className="text-slate-700 whitespace-pre-wrap pr-6">{note.content}</p>
                    <p className="text-xs text-slate-400 mt-4">{formatDate(note.created_at)}</p>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Sponsor Section */}
        <SponsorSection 
          sponsorInfo={sponsorInfo} 
          tokenId={tokenId} 
          moduleType="home" 
          className="mt-8"
        />
      </main>

      <ModuleChat sponsorInfo={sponsorInfo} />

      {/* Add Event Dialog */}
      <Dialog open={showAddEvent} onOpenChange={setShowAddEvent}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Event Title</label>
              <Input
                placeholder="Doctor Appointment, Meeting..."
                value={eventTitle}
                onChange={(e) => setEventTitle(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl"
                data-testid="event-title-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal rounded-xl",
                      !eventDate && "text-muted-foreground"
                    )}
                    data-testid="event-date-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {eventDate ? format(eventDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={eventDate}
                    onSelect={setEventDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Family Member (optional)</label>
              <Input
                placeholder="Dad, Mom, Kids..."
                value={eventMember}
                onChange={(e) => setEventMember(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl"
                data-testid="event-member-input"
              />
            </div>
            <Button
              onClick={handleAddEvent}
              disabled={isSubmitting || !eventTitle || !eventDate}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
              data-testid="confirm-event-btn"
            >
              {isSubmitting ? 'Adding...' : 'Add Event'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Grocery Dialog */}
      <Dialog open={showAddGrocery} onOpenChange={setShowAddGrocery}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Grocery Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Item Name</label>
              <Input
                placeholder="Milk, Bread, Eggs..."
                value={groceryName}
                onChange={(e) => setGroceryName(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl"
                data-testid="grocery-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Quantity</label>
              <Input
                type="number"
                value={groceryQuantity}
                onChange={(e) => setGroceryQuantity(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl"
                data-testid="grocery-qty-input"
              />
            </div>
            <Button
              onClick={handleAddGrocery}
              disabled={isSubmitting || !groceryName}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
              data-testid="confirm-grocery-btn"
            >
              {isSubmitting ? 'Adding...' : 'Add Item'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Medication Dialog */}
      <Dialog open={showAddMedication} onOpenChange={setShowAddMedication}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Medication</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Medicine Name</label>
              <Input
                placeholder="Vitamin C, Paracetamol..."
                value={medName}
                onChange={(e) => setMedName(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl"
                data-testid="med-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Dosage (optional)</label>
              <Input
                placeholder="500mg, 1 tablet..."
                value={medDosage}
                onChange={(e) => setMedDosage(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl"
                data-testid="med-dosage-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Schedule (optional)</label>
              <Input
                placeholder="Daily, Twice daily, As needed..."
                value={medSchedule}
                onChange={(e) => setMedSchedule(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl"
                data-testid="med-schedule-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Family Member (optional)</label>
              <Input
                placeholder="Dad, Mom, Kids..."
                value={medMember}
                onChange={(e) => setMedMember(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl"
                data-testid="med-member-input"
              />
            </div>
            <Button
              onClick={handleAddMedication}
              disabled={isSubmitting || !medName}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
              data-testid="confirm-med-btn"
            >
              {isSubmitting ? 'Adding...' : 'Add Medication'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Sticky Note Dialog */}
      <Dialog open={showAddSticky} onOpenChange={setShowAddSticky}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Sticky Note</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Note Content</label>
              <textarea
                placeholder="Don't forget..."
                value={stickyContent}
                onChange={(e) => setStickyContent(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 min-h-[100px] resize-none focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                data-testid="sticky-content-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Color</label>
              <div className="flex gap-2">
                {Object.keys(STICKY_COLORS).map(color => (
                  <button
                    key={color}
                    onClick={() => setStickyColor(color)}
                    className={cn(
                      "w-10 h-10 rounded-lg border-2 transition-all",
                      STICKY_COLORS[color],
                      stickyColor === color ? "ring-2 ring-slate-900 ring-offset-2" : ""
                    )}
                    data-testid={`sticky-color-${color}`}
                  />
                ))}
              </div>
            </div>
            <Button
              onClick={handleAddSticky}
              disabled={isSubmitting || !stickyContent}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
              data-testid="confirm-sticky-btn"
            >
              {isSubmitting ? 'Adding...' : 'Add Note'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
